// LoginPage — Google Sign-In entry point.
// Rendered inside AuthLayout, which supplies the navy brand header + card shell.
import { useState } from "react";
import { authClient } from "../../lib/auth-client.js";

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.88c2.27-2.09 3.57-5.17 3.57-8.82Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.88-3c-1.08.73-2.46 1.16-4.05 1.16-3.11 0-5.75-2.1-6.69-4.93H1.3v3.09A12 12 0 0 0 12 24Z"
      />
      <path fill="#FBBC05" d="M5.31 14.32a7.2 7.2 0 0 1 0-4.64V6.59H1.3a12 12 0 0 0 0 10.82l4.01-3.09Z" />
      <path
        fill="#EA4335"
        d="M12 4.75c1.76 0 3.34.6 4.58 1.79l3.44-3.44C17.94 1.19 15.24 0 12 0A12 12 0 0 0 1.3 6.59l4.01 3.09C6.25 6.85 8.89 4.75 12 4.75Z"
      />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="h-5 w-5 animate-spin text-dm-slate" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.37 0 0 5.37 0 12h4Z" />
    </svg>
  );
}

export default function LoginPage() {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleGoogleSignIn() {
    setError(null);
    setIsRedirecting(true);
    try {
      // On success, authClient redirects the browser to the Google consent
      // screen itself (data.url + data.redirect) — no manual redirect needed.
      const { error: signInError } = await authClient.signIn.social({
        provider:    "google",
        callbackURL: "/app",
      });
      if (signInError) {
        setError(signInError.message ?? "Unable to reach the sign-in service. Please try again.");
        setIsRedirecting(false);
      }
    } catch {
      setError("Unable to reach the sign-in service. Please try again.");
      setIsRedirecting(false);
    }
  }

  return (
    <div className="text-center">
      <h1 className="font-display text-xl font-semibold text-dm-navy">Sign in to Daniels Mediation LMS</h1>
      <p className="mt-2 text-sm text-dm-slate">
        Access your courses, track attendance, and download certificates
      </p>

      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={isRedirecting}
        className="mt-6 flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-dm-navy shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isRedirecting ? <Spinner /> : <GoogleIcon />}
        {isRedirecting ? "Redirecting to Google…" : "Continue with Google"}
      </button>

      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

      <p className="mt-8 text-xs text-dm-slate">By signing in you agree to our terms</p>
    </div>
  );
}
